import React, { useEffect, useState } from "react";
import { Modal, Select } from "antd";
import {
  HiOutlineCreditCard,
  HiOutlineXMark,
  HiOutlineBuildingOffice2,
  HiOutlineMapPin,
  HiOutlineUser,
} from "react-icons/hi2";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { billingApi, PaymentMethod, BillingInfo } from "../../api";
import { FormButton } from "../../ui";
import { handleFailedRequest, notify } from "../../util";
import { IResponseError } from "../../data-access";
import { COUNTRIES, STATES, getCountryLabel, getStateLabel, validatePostalCode, filterPostalInput } from "./address-data";
import "./payment-details-modal.scss";

interface PaymentDetailsModalProps {
  open: boolean;
  onClose: () => void;
}

// --- Card Update Form (Stripe Elements) ---
const CardForm = ({
  onSuccess,
  onCancel,
}: {
  onSuccess: () => void;
  onCancel: () => void;
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isSaving, setIsSaving] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!stripe || !elements) return;
    const cardElement = elements.getElement(CardElement);
    if (!cardElement) return;

    setIsSaving(true);
    setCardError(null);

    try {
      const { client_secret } = await billingApi.createSetupIntent();
      const result = await stripe.confirmCardSetup(client_secret, {
        payment_method: { card: cardElement },
      });

      if (result.error) {
        setCardError(result.error.message || "Failed to save card.");
        setIsSaving(false);
        return;
      }

      // Set as default payment method
      const pmId = result.setupIntent?.payment_method;
      if (pmId && typeof pmId === "string") {
        await billingApi.setDefaultPaymentMethod(pmId);
      }

      notify("success", "Payment method updated successfully.");
      onSuccess();
    } catch (error) {
      const err = error as IResponseError;
      handleFailedRequest(err.status, err.text, err.details);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="pd-section">
      <h4 className="pd-section__title">Update Card</h4>
      <div className="pd-card-element">
        <CardElement
          options={{
            style: {
              base: {
                fontSize: "14px",
                color: "#ffffff",
                fontFamily: "'Kumbh Sans', sans-serif",
                "::placeholder": { color: "#929EAE" },
              },
              invalid: { color: "#F87171" },
            },
          }}
        />
      </div>
      {cardError && <p className="pd-error">{cardError}</p>}
      <div className="pd-section__buttons">
        <FormButton theme="grey" onClick={onCancel} disabled={isSaving}>
          Cancel
        </FormButton>
        <FormButton theme="green-light" onClick={handleSave} loading={isSaving} disabled={isSaving || !stripe}>
          Save Card
        </FormButton>
      </div>
    </div>
  );
};

// --- Main Modal ---
export const PaymentDetailsModal = ({ open, onClose }: PaymentDetailsModalProps) => {
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(null);
  const [billingInfo, setBillingInfo] = useState<BillingInfo | null>(null);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditingCard, setIsEditingCard] = useState(false);
  const [isEditingBilling, setIsEditingBilling] = useState(false);

  // Form state
  const [isCompany, setIsCompany] = useState(false);
  const [formName, setFormName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [taxValue, setTaxValue] = useState("");
  const [formLine1, setFormLine1] = useState("");
  const [formLine2, setFormLine2] = useState("");
  const [formCity, setFormCity] = useState("");
  const [formState, setFormState] = useState("");
  const [formPostal, setFormPostal] = useState("");
  const [postalError, setPostalError] = useState<string | null>(null);
  const [formCountry, setFormCountry] = useState("");
  const [isSavingBilling, setIsSavingBilling] = useState(false);

  const fetchData = () => {
    setIsLoading(true);
    billingApi
      .getPaymentMethod()
      .then((response) => {
        setPaymentMethod(response.payment_method);
        setBillingInfo(response.billing_info);

        if (response.billing_info) {
          const b = response.billing_info;
          setIsCompany(b.is_company);
          // Populate both name fields so toggling doesn't lose data
          const name = b.name || "";
          setFormName(name);
          setCompanyName(name);
          setTaxValue(b.tax_id?.value || "");
          setFormLine1(b.address.line1 || "");
          setFormLine2(b.address.line2 || "");
          setFormCity(b.address.city || "");
          setFormState(b.address.state || "");
          setFormPostal(b.address.postal_code || "");
          setFormCountry(b.address.country || "");
        }

        if (!stripePromise) {
          const key = response.stripe_public_key || process.env.REACT_APP_STRIPE_PUBLIC_KEY;
          if (key) setStripePromise(loadStripe(key));
        }
      })
      .catch((error: IResponseError) => {
        handleFailedRequest(error.status, error.text, error.details);
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    if (open) {
      fetchData();
      setIsEditingCard(false);
      setIsEditingBilling(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleCardUpdateSuccess = () => {
    setIsEditingCard(false);
    fetchData();
  };

  const handleSaveBilling = async () => {
    // Validate postal code
    if (formPostal && formCountry) {
      const err = validatePostalCode(formPostal, formCountry);
      if (err) {
        setPostalError(err);
        return;
      }
    }
    setPostalError(null);
    setIsSavingBilling(true);
    try {
      // Stripe customer.name = company name (for invoices) or personal name
      const nameForStripe = isCompany ? companyName : formName;

      await billingApi.updateBillingInfo({
        name: nameForStripe,
        is_company: isCompany,
        address: {
          line1: formLine1,
          line2: formLine2,
          city: formCity,
          state: formState,
          postal_code: formPostal,
          country: formCountry,
        },
        tax_id: isCompany && taxValue ? { value: taxValue } : null,
        old_tax_id: billingInfo?.tax_id?.id || undefined,
      });
      notify("success", "Billing info updated.");
      setIsEditingBilling(false);
      fetchData();
    } catch (error) {
      const err = error as IResponseError;
      handleFailedRequest(err.status, err.text, err.details);
    } finally {
      setIsSavingBilling(false);
    }
  };

  const brandLabel = (brand: string) => {
    const brands: Record<string, string> = {
      visa: "Visa", mastercard: "Mastercard", amex: "Amex",
      discover: "Discover", diners: "Diners Club", jcb: "JCB", unionpay: "UnionPay",
    };
    return brands[brand] || brand.charAt(0).toUpperCase() + brand.slice(1);
  };

  const hasAddress = billingInfo && (billingInfo.address.line1 || billingInfo.address.city);

  const formatAddress = () => {
    if (!billingInfo) return "";
    const a = billingInfo.address;
    const stateDisplay = a.state ? getStateLabel(a.state, a.country) : "";
    return [a.line1, a.line2, [a.city, stateDisplay, a.postal_code].filter(Boolean).join(", "), getCountryLabel(a.country)].filter(Boolean).join(", ");
  };

  const formatTaxType = (type: string) => type.replace(/_/g, " ").toUpperCase();

  // --- Address fields (shared between individual & company) ---
  const stateConfig = STATES[formCountry];

  const renderAddressFields = () => (
    <>
      <div className="pd-form__field">
        <label>Country</label>
        <Select
          showSearch
          value={formCountry || undefined}
          onChange={(val) => { setFormCountry(val); setFormState(""); setPostalError(null); }}
          placeholder="Select country"
          options={COUNTRIES}
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase()) ||
            (option?.value ?? "").toLowerCase().includes(input.toLowerCase())
          }
          popupClassName="pd-country-dropdown"
        />
      </div>
      <div className="pd-form__field">
        <label>Address Line 1</label>
        <input value={formLine1} onChange={(e) => setFormLine1(e.target.value)} placeholder="Street address" />
      </div>
      <div className="pd-form__field">
        <label>Address Line 2</label>
        <input value={formLine2} onChange={(e) => setFormLine2(e.target.value)} placeholder="Apt, suite, etc. (optional)" />
      </div>
      <div className="pd-form__row">
        <div className="pd-form__field">
          <label>City</label>
          <input value={formCity} onChange={(e) => setFormCity(e.target.value)} placeholder="City" />
        </div>
        <div className="pd-form__field">
          <label>{stateConfig?.label || "State"}</label>
          {stateConfig ? (
            <Select
              showSearch
              value={formState || undefined}
              onChange={(val) => setFormState(val)}
              placeholder={`Select ${stateConfig.label.toLowerCase()}`}
              options={stateConfig.options}
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase()) ||
                (option?.value ?? "").toLowerCase().includes(input.toLowerCase())
              }
              popupClassName="pd-country-dropdown"
            />
          ) : (
            <input value={formState} onChange={(e) => setFormState(e.target.value)} placeholder="State / Province" />
          )}
        </div>
      </div>
      <div className="pd-form__field">
        <label>Postal Code</label>
        <input
          value={formPostal}
          onChange={(e) => { setFormPostal(filterPostalInput(e.target.value, formCountry)); setPostalError(null); }}
          placeholder="ZIP / Postal code"
          className={postalError ? "pd-form__input--error" : ""}
        />
        {postalError && <span className="pd-form__error">{postalError}</span>}
      </div>
    </>
  );

  return (
    <Modal
      open={open}
      className="payment-details-ant-modal"
      closeIcon={<HiOutlineXMark size={20} />}
      onCancel={onClose}
      footer={null}
      title={null}
      width={480}
      destroyOnClose
    >
      {isLoading ? (
        <div className="pd-modal">
          <div className="pd-modal__shimmer-icon" />
          <div className="pd-modal__shimmer-line" />
          <div className="pd-modal__shimmer-line pd-modal__shimmer-line--short" />
          <div className="pd-modal__shimmer-line" />
        </div>
      ) : (
        <div className="pd-modal">
          <div className="pd-modal__header">
            <div className="pd-modal__header-icon">
              <HiOutlineCreditCard size={28} />
            </div>
            <h3 className="pd-modal__title">Payment Details</h3>
          </div>

          {/* ── Card ── */}
          {isEditingCard && stripePromise ? (
            <Elements stripe={stripePromise}>
              <CardForm
                onSuccess={handleCardUpdateSuccess}
                onCancel={() => setIsEditingCard(false)}
              />
            </Elements>
          ) : (
            <div className="pd-section">
              <div className="pd-section__header">
                <h4 className="pd-section__title">Card</h4>
                <button className="pd-section__edit-btn" onClick={() => setIsEditingCard(true)}>
                  {paymentMethod ? "Update" : "Add"}
                </button>
              </div>
              {paymentMethod ? (
                <div className="pd-card-row">
                  <div className="pd-card-row__left">
                    <HiOutlineCreditCard size={18} />
                    <span>{brandLabel(paymentMethod.brand)} &bull;&bull;&bull;&bull; {paymentMethod.last4}</span>
                  </div>
                  <span className="pd-card-row__exp">
                    Exp {String(paymentMethod.exp_month).padStart(2, "0")}/{String(paymentMethod.exp_year).slice(-2)}
                  </span>
                </div>
              ) : (
                <p className="pd-section__empty">No card on file</p>
              )}
            </div>
          )}

          {/* ── Billing Details (edit) ── */}
          {isEditingBilling ? (
            <div className="pd-section">
              <h4 className="pd-section__title">Billing Details</h4>
              <div className="pd-form">
                {/* Toggle: individual / company */}
                <div className="pd-form__toggle-row">
                  <HiOutlineBuildingOffice2 size={18} />
                  <span>Bill as company</span>
                  <label className="pd-toggle">
                    <input type="checkbox" checked={isCompany} onChange={(e) => setIsCompany(e.target.checked)} />
                    <span className="pd-toggle__slider" />
                  </label>
                </div>

                {isCompany ? (
                  /* ── Company fields ── */
                  <>
                    <div className="pd-form__field" style={{ marginTop: 12 }}>
                      <label>Company Name</label>
                      <input value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Company name (appears on invoices)" />
                    </div>
                    <div className="pd-form__field">
                      <label>Tax ID</label>
                      <input value={taxValue} onChange={(e) => setTaxValue(e.target.value)} placeholder="e.g. DE123456789" />
                      <span className="pd-form__hint">Type is determined automatically from your country</span>
                    </div>
                    <div className="pd-form__divider" />
                    {renderAddressFields()}
                  </>
                ) : (
                  /* ── Individual fields ── */
                  <>
                    <div className="pd-form__field" style={{ marginTop: 12 }}>
                      <label>Full Name</label>
                      <input value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="Full name (appears on invoices)" />
                    </div>
                    <div className="pd-form__divider" />
                    {renderAddressFields()}
                  </>
                )}
              </div>
              <div className="pd-section__buttons">
                <FormButton theme="grey" onClick={() => setIsEditingBilling(false)} disabled={isSavingBilling}>
                  Cancel
                </FormButton>
                <FormButton theme="green-light" onClick={handleSaveBilling} loading={isSavingBilling} disabled={isSavingBilling}>
                  Save
                </FormButton>
              </div>
            </div>
          ) : (
            /* ── Billing Details (view) ── */
            <div className="pd-section">
              <div className="pd-section__header">
                <h4 className="pd-section__title">Billing Details</h4>
                <button className="pd-section__edit-btn" onClick={() => setIsEditingBilling(true)}>
                  Edit
                </button>
              </div>
              {billingInfo?.name || hasAddress || billingInfo?.tax_id ? (
                <>
                  {billingInfo?.name && (
                    <div className="pd-info-row">
                      {billingInfo.is_company ? <HiOutlineBuildingOffice2 size={18} /> : <HiOutlineUser size={18} />}
                      <span className="pd-info-row__name">{billingInfo.name}</span>
                    </div>
                  )}
                  {billingInfo?.tax_id && (
                    <div className="pd-info-row" style={{ marginTop: 4 }}>
                      <HiOutlineCreditCard size={18} />
                      <span>{formatTaxType(billingInfo.tax_id.type)} · {billingInfo.tax_id.value}</span>
                    </div>
                  )}
                  {hasAddress && (
                    <div className="pd-info-row" style={{ marginTop: 4 }}>
                      <HiOutlineMapPin size={18} />
                      <span>{formatAddress()}</span>
                    </div>
                  )}
                </>
              ) : (
                <p className="pd-section__empty">No billing details</p>
              )}
            </div>
          )}

          {/* Close */}
          {!isEditingCard && !isEditingBilling && (
            <FormButton theme="grey" className="pd-modal__close-btn" onClick={onClose}>
              Close
            </FormButton>
          )}
        </div>
      )}
    </Modal>
  );
};
